import { useEffect, useState } from "react";
import { motion, useReducedMotion, useMotionValue, useTransform, useSpring } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Globe2, ShieldCheck, Cloud, Laptop, Server, Brain, Network } from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import AnimatedBackground from "@/components/AnimatedBackground";

const MaskedLine = ({ children, delay }) => (
    <span className="block overflow-hidden pb-1">
        <motion.span
            className="block"
            initial={{ y: "112%" }}
            animate={{ y: 0 }}
            transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
        >
            {children}
        </motion.span>
    </span>
);

const NODES = [
    { Icon: Laptop, label: "Digital Workplace", x: 14, y: 9 },
    { Icon: Cloud, label: "Cloud & Infrastructure", x: 86, y: 10 },
    { Icon: ShieldCheck, label: "Cybersecurity", x: 95, y: 48 },
    { Icon: Server, label: "Managed Services", x: 85, y: 88 },
    { Icon: Brain, label: "AI & Data", x: 14, y: 89 },
    { Icon: Network, label: "Infrastructure & Networks", x: 4, y: 50 },
];

const OrbitVisual = () => {
    const reduce = useReducedMotion();
    return (
        <div className="relative mx-auto aspect-square w-full max-w-[580px]" data-testid="hero-orbit-visual">
            <div className="animate-pulse-glow absolute inset-[16%] rounded-full bg-electric/15 blur-3xl" aria-hidden="true" />
            <div className="absolute inset-[9%] rounded-full border t-border" aria-hidden="true" />
            <div className="absolute inset-[23%] rounded-full border border-electric/25" aria-hidden="true" />
            <svg className="absolute inset-0 h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
                {NODES.map((n) => (
                    <line
                        key={n.label}
                        x1="50"
                        y1="50"
                        x2={n.x}
                        y2={n.y}
                        stroke="#1E90FF"
                        strokeOpacity="0.28"
                        strokeWidth="0.25"
                        strokeDasharray="2 3"
                        className="animate-dashflow motion-reduce:[animation:none]"
                    />
                ))}
            </svg>
            <div className="absolute left-1/2 top-1/2 flex h-[30%] w-[30%] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-electric/40 bg-gradient-to-br from-navy-800 to-navy-950 shadow-[0_0_80px_-12px_rgba(30,144,255,0.55)]">
                <motion.img
                    src="/v-mark-hero.webp"
                    alt="Varasun V mark"
                    className="pointer-events-none w-[80%] select-none"
                    style={{ filter: "drop-shadow(0 0 26px rgba(30,144,255,0.5))" }}
                    animate={reduce ? {} : { scale: [1, 1.04, 1] }}
                    transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                />
            </div>
            {NODES.map((n, i) => (
                <motion.div
                    key={n.label}
                    className="absolute flex w-24 -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-2 text-center"
                    style={{ left: `${n.x}%`, top: `${n.y}%` }}
                    animate={reduce ? {} : { y: [0, -6, 0] }}
                    transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut", delay: i * 0.6 }}
                >
                    <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-electric/30 bg-navy-900/90 text-electric shadow-[0_0_24px_-6px_rgba(30,144,255,0.5)] backdrop-blur sm:h-12 sm:w-12">
                        <n.Icon size={18} />
                    </span>
                    <span className="font-mono text-[8.5px] font-semibold uppercase leading-tight tracking-[0.14em] t-soft sm:text-[10px]">
                        {n.label}
                    </span>
                </motion.div>
            ))}
            <div className="animate-beam pointer-events-none absolute inset-y-[46%] left-[-10%] h-[2px] w-[40%] bg-gradient-to-r from-transparent via-azure to-transparent" aria-hidden="true" />
        </div>
    );
};

const Hero = () => {
    const reduce = useReducedMotion();
    const [loadVideo, setLoadVideo] = useState(false);

    useEffect(() => {
        if (reduce) return undefined;
        const ric = window.requestIdleCallback;
        if (ric) {
            const id = ric(() => setLoadVideo(true), { timeout: 2500 });
            return () => window.cancelIdleCallback?.(id);
        }
        const t = setTimeout(() => setLoadVideo(true), 1200);
        return () => clearTimeout(t);
    }, [reduce]);
    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const sx = useSpring(mx, { stiffness: 50, damping: 20 });
    const sy = useSpring(my, { stiffness: 50, damping: 20 });
    const px = useTransform(sx, [-0.5, 0.5], [-14, 14]);
    const py = useTransform(sy, [-0.5, 0.5], [-10, 10]);

    const onMouseMove = (e) => {
        if (reduce) return;
        const r = e.currentTarget.getBoundingClientRect();
        mx.set((e.clientX - r.left) / r.width - 0.5);
        my.set((e.clientY - r.top) / r.height - 0.5);
    };

    return (
        <section
            data-testid="hero-section"
            onMouseMove={onMouseMove}
            className="media-dark bg-grid-dark relative flex min-h-[88vh] items-center overflow-hidden t-bg pt-[96px]"
        >
            {/* Poster paints instantly (LCP-friendly); the decorative video is deferred until idle. */}
            <img
                src="/media/varasun-hero-poster.jpg"
                alt=""
                aria-hidden="true"
                className="absolute inset-0 h-full w-full object-cover opacity-45"
            />
            {loadVideo && (
                <video
                    className="absolute inset-0 h-full w-full object-cover opacity-45"
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="auto"
                    poster="/media/varasun-hero-poster.jpg"
                    onCanPlay={(e) => e.currentTarget.play().catch(() => {})}
                    aria-hidden="true"
                >
                    <source src="/media/varasun-hero-network.mp4" type="video/mp4" />
                </video>
            )}
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-navy-950 via-navy-950/70 to-navy-950/40" aria-hidden="true" />
            <AnimatedBackground className="absolute inset-0 h-full w-full opacity-50" />
            <div className="animate-aurora motion-reduce:[animation:none] pointer-events-none absolute -left-40 top-[-20%] h-[36rem] w-[36rem] rounded-full bg-electric/10 blur-[120px]" aria-hidden="true" />
            <div className="animate-aurora-slow motion-reduce:[animation:none] pointer-events-none absolute -right-40 bottom-[-30%] h-[32rem] w-[32rem] rounded-full bg-azure/8 blur-[120px]" aria-hidden="true" />
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_45%,#070A12_100%)]" />

            <div className="container-x relative z-10 grid items-center gap-16 py-16 lg:grid-cols-[1.15fr_0.85fr] lg:py-16">
                <div>
                    <p
                        className="eyebrow eyebrow-intro font-hero !text-[13px] !font-bold md:!text-sm"
                        data-testid="hero-eyebrow"
                    >
                        Enterprise Technology Transformation Partner
                    </p>

                    <h1 className="mt-6 font-hero text-4xl font-bold leading-[1.06] tracking-tight t-heading sm:text-5xl lg:text-[4.35rem]">
                        <MaskedLine delay={0.25}>Transforming Technology</MaskedLine>
                        <MaskedLine delay={0.4}>
                            Into <span className="text-gradient">Business Advantage</span>
                        </MaskedLine>
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.75 }}
                        className="mt-7 max-w-xl text-base leading-relaxed t-soft md:text-lg"
                        data-testid="hero-subtext"
                    >
                        A U.S.-based technology partner for <span className="font-semibold t-heading">Managed IT, Cloud,
                        Cybersecurity and Microsoft 365</span> — helping businesses run secure, reliable and scalable
                        technology, backed by one accountable team.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.8, delay: 0.85 }}
                        className="mt-6 flex flex-wrap gap-2"
                        data-testid="hero-services"
                    >
                        {["Managed IT", "Cloud & Infrastructure", "Cybersecurity", "Microsoft 365", "Service Desk", "Technology Consulting"].map((s) => (
                            <span
                                key={s}
                                className="rounded-full border border-electric/25 bg-electric/10 px-3 py-1 font-mono text-[10.5px] font-semibold uppercase tracking-wider text-azure"
                            >
                                {s}
                            </span>
                        ))}
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.9 }}
                        className="mt-10 flex flex-wrap items-center gap-4"
                    >
                        <Link to="/contact" data-testid="hero-talk-to-expert-btn" className="btn-primary">
                            Schedule a Consultation <ArrowRight size={16} />
                        </Link>
                        <button onClick={() => scrollToId("capabilities")} data-testid="hero-explore-capabilities-btn" className="btn-ghost">
                            Explore Our Services
                        </button>
                    </motion.div>

                    <motion.ul
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.8, delay: 1.1 }}
                        className="mt-12 flex flex-wrap gap-x-8 gap-y-3"
                        data-testid="hero-highlights"
                    >
                        {[
                            { Icon: Globe2, text: "US-based, globally capable delivery" },
                            { Icon: ShieldCheck, text: "Security by design" },
                            { Icon: Cloud, text: "Cloud, AI & data engineering" },
                        ].map(({ Icon, text }) => (
                            <li key={text} className="flex items-center gap-2.5 text-sm t-soft">
                                <Icon size={15} className="text-electric" />
                                {text}
                            </li>
                        ))}
                    </motion.ul>
                </div>

                <motion.div
                    initial={{ opacity: 0, scale: 0.94 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 1.2, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
                    style={{ x: px, y: py }}
                    className="mt-14 lg:mt-0"
                >
                    <OrbitVisual />
                </motion.div>
            </div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.6, duration: 1 }}
                className="absolute bottom-8 left-1/2 hidden -translate-x-1/2 md:block"
                aria-hidden="true"
            >
                <div className="flex h-12 w-7 items-start justify-center rounded-full border t-border p-1.5">
                    <motion.div
                        className="h-2 w-2 rounded-full bg-electric"
                        animate={reduce ? {} : { y: [0, 22, 0] }}
                        transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
                    />
                </div>
            </motion.div>
        </section>
    );
};

export default Hero;
